/**
 * Created by rhinov15 on 16/09/2016.
 * Cocos node that displays the sun showing its position according to the current map being drawn
 */

import { EntityNode } from "./EntityNode";
import { GameObjectManager } from "../GameObjectManager";
import { PlanManager } from "../../../../cocosPlan";
import { Commands, Events, eventsManager, math, Sun } from "@rhinov/savane-js";

declare var cc: any;

export class SunNode extends EntityNode {
    constructor(sun: Sun) {
        super(sun);

        // We store the sun savane entity for update computations
        this.entity = sun;

        // Let's create a Cocos node with a yellow circle (symbol for the sun in the tool) size 50, 50
        this.sun = new cc.DrawNode();
        this.sun.drawDot(new cc.p(0, 0), 50, new cc.color('#fbe800'));
        this.sun.setContentSize(50, 50);

        // Update everything to copute other values
        this.update();
        // Add this cocos child to the current node
        this.addChild(this.sun, 1);
        this.pressDownTime = null;

        /**
         * Custom listener for mouse events
         *
         */

        // This will help to know if the mouse is dragged to move the sun
        this.dragged = false;
        this.clicked = false;
        this.sunClickX = 0;
        this.sunClickY = 0;

        // Mouse listener
        this.mouseListener = cc.eventManager.addListener(
            {
                event: cc.EventListener.MOUSE,
                swallowTouches: false,

                // Mouse click up listener
                onMouseUp: function (this: SunNode, _event: any) {
                    // If the sun isn't displayed or if sun not clicked then nothing to do
                    if (!PlanManager.getInstance().showSun || !this.clicked) {
                        return;
                    }

                    // The sun is no longer clicked
                    this.clicked = false;

                    // Let's check if the sun was dragged then display the sun tulip for settings display
                    if (!this.dragged) {
                        // Show sun's tulip
                        PlanManager.getInstance().updateItemsSelected([this.entity], true, true);
                    }
                    else {
                        // If we were dragging the sun to move it then this is finished with a click up
                        this.dragged = false;

                        PlanManager.getInstance().executeCommand(new Commands.EditSunCommand(this.entity));

                        eventsManager.instance.dispatch(Events.SUN_DRAG_OFF);
                    }
                }.bind(this),

                // Mouse click down listener
                onMouseDown: function (this: SunNode, event: any) {
                    // If the sun isn't displayed or if sun not dragged then nothing to do
                    if (!PlanManager.getInstance().showSun) {
                        return;
                    }

                    if ((event._button === 2) && (this.clicked)) {
                        this.clicked = false;
                        this.dragged = false;
                        this.entity.endTemporary();

                        // The modification of the south direction was cancelled, update webGL
                        eventsManager.instance.dispatch(Events.ENTITY_PARAM_EDITED,
                            {
                                id: this.entity.id,
                                type: this.entity.entityType,
                                entity: this.entity,
                                key: "southDirection"
                            });

                        return;
                    }

                    // Get mouse cursor position
                    var pos = new cc.p(event.getLocationX(), event.getLocationY());
                    // Convert absolute point to sun context
                    pos = this.sun.convertToNodeSpace(pos);

                    // Click within the sun circle ?
                    if (Math.abs(pos.x) < this.sun.getContentSize().width && Math.abs(pos.y) < this.sun.getContentSize().height) {
                        // Yes then the sun is dragged
                        this.clicked = true;
                        // Store click position
                        this.sunClickX = event.getLocationX();
                        this.sunClickY = event.getLocationY();
                    }
                }.bind(this),

                // Mouse cursor move listener
                onMouseMove: function (this: SunNode, event: any) {
                    // If the sun isn't displayed or if sun not dragged then nothing to do
                    if (!PlanManager.getInstance().showSun) {
                        return;
                    }

                    // If the cocos node is dragged (click down inside the cocos node)
                    if (this.clicked) {
                        if ((this.sunClickX !== event.getLocationX()) || (this.sunClickY !== event.getLocationY())) {
                            this.dragged = true;
                            // Start a temporary sun if clicked
                            this.entity.startTemporary();
                            // Sun dragged
                            eventsManager.instance.dispatch(Events.SUN_DRAG_ON);
                        }

                        // Currenttly dragged
                        var pos = new cc.p(event.getLocationX(), event.getLocationY());
                        pos = PlanManager.getInstance().scrollWindow.grid.convertToNodeSpace(pos);

                        var bbox = this.entity.parent.boundingBox;
                        var topLeft = GameObjectManager.convertToCocosCoord(bbox[0]);
                        var botRight = GameObjectManager.convertToCocosCoord(bbox[3]);
                        var center = new cc.p(botRight.x - topLeft.x, botRight.y - topLeft.y);
                        center.x = topLeft.x + center.x * 0.5;
                        center.y = topLeft.y + center.y * 0.5;

                        // Dist center to point
                        var newDir = new cc.p(center.x - pos.x, center.y - pos.y);
                        //Normalize
                        newDir = cc.pNormalize(newDir);

                        var cVec = math.vec2.create();
                        math.vec2.set(cVec, newDir.x, newDir.y);
                        this.entity.southDirection = cVec;
                        this.update();

                        // We change the sun south parameter with the computed above
                        eventsManager.instance.dispatch(Events.ENTITY_PARAM_EDITED,
                            {
                                id: this.entity.id,
                                type: this.entity.entityType,
                                entity: this.entity,
                                key: "southDirection"
                            });
                    }
                }.bind(this)
            }, this);
    }

    // Sun display update, prepare next frame
    update() {
        // If the sun isn't displayed or if sun not dragged then nothing to do
        if (!PlanManager.getInstance().showSun) {
            // Mark the node not to be displayed
            this.setVisible(false);
            // Then leave
            return;
        }

        // Clear the previous display of the node
        this.clear();
        // Get the floor bounding box
        var bbox = this.entity.parent.boundingBox;
        // Get top/left
        var topLeft = GameObjectManager.convertToCocosCoord(bbox[0]);
        // Get bottom/right
        var botRight = GameObjectManager.convertToCocosCoord(bbox[3]);
        // Compute a radius that englobes everything
        var radius = cc.pDistance(topLeft, botRight) * 0.5;
        // Add a bit of space
        radius += 100;
        // Compute the center of the current map
        var center = new cc.p(botRight.x - topLeft.x, botRight.y - topLeft.y);
        center.x = topLeft.x + center.x * 0.5;
        center.y = topLeft.y + center.y * 0.5;
        // Display a simple circle as the orbit of the floor
        this.drawCircle(center, radius, Math.PI * 2, 100, false, 3, new cc.color('#fbe800'));

        // Get the south direction of the sun
        var southDirection = this.entity.southDirection;
        // Set the correct position for the full circle created in the ctor
        this.sun.setPosition(new cc.p(center.x - southDirection[0] * (radius + 12), center.y - southDirection[1] * (radius + 12)));
        // And display it
        this.setVisible(true);
    }

    // Sun cocos node destruction
    onExit() {
        // Destroy parent class
        super.onExit();
        // Remove the mouse listeners
        cc.eventManager.removeListener(this.mouseListener);
    }
}


